import type { Route } from "./+types/$locale.partners";
import { buildSeoMeta } from "@/components/seo/meta";
import { ORG_ID, WEBSITE_ID } from "@/components/seo";
import { env } from "@/lib/env";
import { partnersResourceHints } from "@/components/partners/partnersResourceHints";
import { PartnersPage } from "@/features/partners/routes/PartnersPage";

/**
 * No loader: `<PartnersPage>` renders only static partner content (no client-
 * side data fetching), so a runtime `useQuery` cache seed would have nothing
 * to seed. The meta export is enough to give the prerendered HTML SEO tags.
 *
 * Static WebPage + partnership Service JSON-LD that previously lived in the
 * helmet-based `<PartnersStructuredData>` is emitted from this meta export
 * instead. The dynamic partner ItemList (which was sourced from a runtime
 * hook) is intentionally dropped here — adding it back would require a
 * route-level loader fetching the partner list. Tracked as a follow-up.
 */

export function meta({ params }: Route.MetaArgs) {
    const locale = (params.locale as string) ?? "en";
    const partnersUrl = `${env.SITE_URL}/${locale}/partners`;

    const resourceHintDescriptors = partnersResourceHints.map((hint) => ({
        tagName: "link" as const,
        rel: hint.rel,
        href: hint.href,
        ...(hint.as ? { as: hint.as } : {}),
        ...(hint.type ? { type: hint.type } : {}),
        ...(hint.crossOrigin ? { crossOrigin: hint.crossOrigin } : {}),
    }));

    return [
        ...resourceHintDescriptors,
        ...buildSeoMeta({
            title: "Partner With Us — Stork Rentals",
            description:
                "Join the Stork Rentals partner network to offer baby equipment rentals to your customers. Increase revenue and customer satisfaction with our seamless platform.",
            path: "/partners",
            keywords:
                "baby equipment rental partner, hotel partner program, accommodation partner, rental partnership",
            // TODO Phase 6: hydrate availableLocales from root loader manifest
            locale,
            availableLocales: [{ code: "en" }],
            jsonLd: {
                "@context": "https://schema.org",
                "@type": "WebPage",
                name: "Partner With Stork Rentals - Baby Equipment Rental Partnership",
                description:
                    "Join Stork Rentals' partner network. Hotels, travel agencies, and car rental companies can offer premium baby equipment to their customers.",
                url: partnersUrl,
                isPartOf: { "@id": WEBSITE_ID },
                mainEntity: {
                    "@type": "Service",
                    name: "Stork Rentals Partnership Program",
                    description:
                        "Partner with Stork Rentals to offer baby equipment rental to your customers",
                    provider: { "@id": ORG_ID },
                    serviceType: "Baby Equipment Rental Partnership",
                },
            },
        }),
    ];
}

export default function PartnersRoute() {
    return <PartnersPage />;
}
